'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  organization: string;
  content: string;
  image: string;
  alt: string;
  rating: number;
}

const mockTestimonials: Testimonial[] = [
{
  id: 1,
  name: "Dr. Sarah Mitchell",
  role: "Chief of Surgery",
  organization: "Metropolitan Medical Center",
  content: "AIVA Medical Supply has been our trusted partner for over 10 years. Their C-Arm systems are exceptional, and the technical support team is always responsive. The equipment quality directly impacts our surgical outcomes.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1de568edf-1763299857042.png",
  alt: "Professional headshot of female surgeon in white coat with stethoscope",
  rating: 5
},
{
  id: 2,
  name: "James Rodriguez",
  role: "Director of Procurement",
  organization: "Regional Healthcare Network",
  content: "The comprehensive service packages and competitive pricing make AIVA our first choice for medical equipment. Their compliance documentation is always thorough, making our procurement process seamless.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1efc8e583-1763299782879.png",
  alt: "Professional headshot of Hispanic male healthcare administrator in business attire",
  rating: 5
},
{
  id: 3,
  name: "Dr. Emily Chen",
  role: "Medical Director",
  organization: "Advanced Surgical Institute",
  content: "Outstanding equipment quality and exceptional customer service. AIVA's training programs ensure our staff can maximize the capabilities of every system. Their commitment to excellence is evident in everything they do.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_144fe309e-1763299127752.png",
  alt: "Professional headshot of Asian female doctor in white medical coat smiling",
  rating: 5
}];


export default function TestimonialCarousel() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % mockTestimonials.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [isHydrated]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + mockTestimonials.length) % mockTestimonials.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % mockTestimonials.length);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-8">
              Trusted by Healthcare Professionals
            </h2>
            <div className="bg-card rounded-lg p-8 shadow-brand">
              <p className="text-lg text-text-secondary italic">
                {mockTestimonials[0].content}
              </p>
            </div>
          </div>
        </div>
      </section>);

  }

  const currentTestimonial = mockTestimonials[currentIndex];

  return (
    <section 
      className="py-16 lg:py-24 relative bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=2070")',
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-secondary/85"></div>
      <div className="relative z-10 max-w-4xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-4">
            <span className="text-sm font-semibold text-white">Client Testimonials</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white">
            Trusted by Healthcare Professionals
          </h2>
        </div>

        <div className="relative">
          <div className="bg-white/95 backdrop-blur-sm rounded-lg p-8 lg:p-12 shadow-brand">
            <div className="flex justify-center mb-6">
              {[...Array(currentTestimonial.rating)].map((_, i) =>
              <Icon key={i} name="StarIcon" size={24} variant="solid" className="text-yellow-400" />
              )}
            </div>

            <blockquote className="text-lg lg:text-xl text-text-primary text-center mb-8 italic leading-relaxed">
              "{currentTestimonial.content}"
            </blockquote>

            <div className="flex items-center justify-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-primary">
                <AppImage
                  src={currentTestimonial.image}
                  alt={currentTestimonial.alt}
                  className="w-full h-full object-cover" />

              </div>
              <div className="text-left">
                <div className="font-bold text-foreground">{currentTestimonial.name}</div>
                <div className="text-sm text-text-secondary">{currentTestimonial.role}</div>
                <div className="text-sm text-primary font-semibold">{currentTestimonial.organization}</div>
              </div>
            </div>
          </div>

          <button
            onClick={handlePrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 w-12 h-12 flex items-center justify-center bg-white hover:bg-primary rounded-full shadow-md transition-all duration-300 group"
            aria-label="Previous testimonial">

            <Icon name="ChevronLeftIcon" size={24} className="text-primary group-hover:text-white" />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 w-12 h-12 flex items-center justify-center bg-white hover:bg-primary rounded-full shadow-md transition-all duration-300 group"
            aria-label="Next testimonial">

            <Icon name="ChevronRightIcon" size={24} className="text-primary group-hover:text-white" />
          </button>

          <div className="flex justify-center gap-2 mt-8">
            {mockTestimonials.map((_, index) =>
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/75'}`
              }
              aria-label={`Go to testimonial ${index + 1}`} />

            )}
          </div>
        </div>
      </div>
    </section>);

}